import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  CreditCard,
  FileHeart,
  Info,
  LifeBuoy,
  Mail,
  MapPin,
  MessageCircle,
  Pill,
  Search,
  ShieldAlert,
  ShieldCheck,
  Stethoscope,
  UserRound,
  Wrench,
} from "@/icons";
import AfriSwitch from "@/components/ui/AfriSwitch.jsx";
import Button from "@/components/ui/Button.jsx";
import Select from "@/components/ui/Select.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supportService from "@/services/supportService.js";
import {
  supportPriorityBadgeClass,
  supportStatusBadgeClass,
} from "@/pages/supportTicketUi.js";

import supportContact from '@/config/support-contact.json';
import { supportFailure, supportAvailability, supportCopy } from '@/lib/supportPresentation.mjs';
import useSupportLanguage from '@/pages/help/useSupportLanguage.jsx';
import SupportRequestThread from './SupportRequestThread.jsx';

const DEFAULT_NEW_TICKET = {
  subject: "",
  description: "",
  type: "general_inquiry",
  priority: "medium",
};

const HELP_ROLE_OPTIONS = [
  { key: "patient", label: "Patient" },
  { key: "doctor", label: "Doctor" },
  { key: "pharmacy", label: "Pharmacy" },
];

const HELP_ROLE_OPTIONS_FR = [
  { key: "patient", label: "Patient" },
  { key: "doctor", label: "Médecin" },
  { key: "pharmacy", label: "Pharmacie" },
];

const HELP_SUPPORT_FR_COPY = {
  "helpSupport.title": "Aide et assistance",
  "helpSupport.eyebrow": "Assistance du Centre d’aide",
  "helpSupport.subtitle":
    "Obtenez de l’aide pour votre compte AfriMed™, vos rendez-vous, messages, prescriptions, paiements et accès.",
  "helpSupport.loading": "Chargement des demandes d’assistance...",
  "helpSupport.common.recently": "Récemment",
  "helpSupport.auth.title": "Authentification requise",
  "helpSupport.auth.body": "Veuillez vous connecter pour accéder à l’aide et à l’assistance.",
  "helpSupport.auth.signIn": "Se connecter",
  "helpSupport.actions.newRequest": "Nouvelle demande d’assistance",
  "helpSupport.actions.browseHelpCentre": "Parcourir le Centre d’aide",
  "helpSupport.actions.supportPending": "Accès à l’assistance en attente",
  "helpSupport.actions.supportPendingTitle":
    "Les demandes d’assistance ne sont pas encore activées pour ce compte.",
  "helpSupport.filters.searchPlaceholder": "Rechercher des demandes",
  "helpSupport.filters.all": "Toutes les demandes",
  "helpSupport.filters.open": "Ouvertes",
  "helpSupport.filters.inProgress": "En cours",
  "helpSupport.filters.resolved": "Résolues",
  "helpSupport.filters.closed": "Clôturées",
  "helpSupport.empty.title": "Aucune demande d’assistance pour le moment.",
  "helpSupport.empty.filteredTitle": "Aucune demande correspondante",
  "helpSupport.empty.filtered": "Aucune demande ne correspond à votre recherche ou filtre actuel.",
  "helpSupport.empty.default":
    "Créez une demande d’assistance si vous avez besoin d’un examen par l’équipe AfriMed™.",
  "helpSupport.errors.permission":
    "Impossible de charger les demandes d’assistance pour le moment. Vous pouvez réessayer ou créer une nouvelle demande si le problème persiste.",
  "helpSupport.errors.loadData": "Impossible de charger les données d’assistance.",
  "helpSupport.errors.loadInformation": "Impossible de charger les informations d’assistance.",
  "helpSupport.errors.requiredFields": "Veuillez remplir tous les champs requis.",
  "helpSupport.errors.createTicket": "Impossible de créer la demande d’assistance.",
  "helpSupport.accessPending.title": "Accès à l’assistance en attente",
  "helpSupport.notice.title": "Information d’assistance",
  "helpSupport.supportOptions.title": "De quoi avez-vous besoin ?",
  "helpSupport.supportOptions.description":
    "Choisissez le domaine le plus proche de votre problème. L’assistance AfriMed™ aide pour l’accès, les dossiers, les rendez-vous, les paiements et les questions techniques.",
  "helpSupport.supportOptions.doctorDescription":
    "Choisissez le domaine clinicien le plus proche de votre problème. L’assistance AfriMed™ aide pour les profils médecins, la vérification, les rendez-vous, les messages, les dossiers et les questions techniques.",
  "helpSupport.supportOptions.pharmacyDescription":
    "Choisissez le domaine pharmacie le plus proche de votre problème. L’assistance AfriMed™ aide pour l’onboarding, les officines, le stock, l’exécution des prescriptions, les paiements, l’équipe et les questions techniques.",
  "helpSupport.categories.account.title": "Accès au compte",
  "helpSupport.categories.account.description":
    "Pour les problèmes de connexion, OTP, mot de passe, email ou vérification du téléphone.",
  "helpSupport.categories.appointments.title": "Rendez-vous",
  "helpSupport.categories.appointments.description":
    "Pour l’aide concernant la réservation, la modification, la consultation vidéo, le clinicien ou les rendez-vous des personnes à charge.",
  "helpSupport.categories.ehr.title": "Prescriptions et mon DME",
  "helpSupport.categories.ehr.description":
    "Pour l’accès aux prescriptions, la visibilité du dossier de santé, la vérification ou les questions de téléchargement.",
  "helpSupport.categories.payments.title": "Paiements et assurance",
  "helpSupport.categories.payments.description":
    "Pour les questions de moyen de paiement, lien sponsor, reçu, facture ou sélection d’assurance.",
  "helpSupport.categories.messages.title": "Messages et Fola",
  "helpSupport.categories.messages.description":
    "Pour les fils de messages, l’aide Fola, le support linguistique et ce que Fola peut ou ne peut pas faire.",
  "helpSupport.categories.technical.title": "Assistance technique",
  "helpSupport.categories.technical.description":
    "Pour les problèmes de navigateur, appareil, notification, téléversement ou affichage.",
  "helpSupport.categories.doctorProfile.title": "Configuration du profil médecin",
  "helpSupport.categories.doctorProfile.description":
    "Pour le profil clinicien, la spécialité, les langues, la pratique et la configuration des réglages.",
  "helpSupport.categories.doctorVerification.title": "Vérification et justificatifs",
  "helpSupport.categories.doctorVerification.description":
    "Pour l’identité/passeport, l’inscription, le téléversement des justificatifs et les questions de vérification en attente.",
  "helpSupport.categories.doctorAvailability.title": "Disponibilités et lieux de consultation",
  "helpSupport.categories.doctorAvailability.description":
    "Pour la configuration des lieux de pratique, services, consultations et disponibilités.",
  "helpSupport.categories.doctorAppointments.title": "Rendez-vous",
  "helpSupport.categories.doctorAppointments.description":
    "Pour examiner les demandes de rendez-vous, les détails de visite et la planification du tableau de bord médecin.",
  "helpSupport.categories.doctorMessages.title": "Messages, orientations et accès EHR",
  "helpSupport.categories.doctorMessages.description":
    "Pour les messages patients, les workflows d’orientation et les demandes d’accès au DME.",
  "helpSupport.categories.doctorPrescriptions.title": "Prescriptions, Fola et notes SOAP",
  "helpSupport.categories.doctorPrescriptions.description":
    "Pour les prescriptions, l’aide Fola clinicien, VitalRep™ et les workflows de notes.",
  "helpSupport.categories.pharmacyProfile.title": "Configuration du profil pharmacie",
  "helpSupport.categories.pharmacyProfile.description":
    "Pour le profil de l’officine, la vérification, les paiements, la préparation au lancement et l’accès propriétaire.",
  "helpSupport.categories.pharmacyStock.title": "Stock et inventaire",
  "helpSupport.categories.pharmacyStock.description":
    "Pour le stock de démonstration, l’ajout ou la modification d’articles et la visibilité de l’inventaire.",
  "helpSupport.categories.pharmacyFulfilment.title": "Exécution des prescriptions",
  "helpSupport.categories.pharmacyFulfilment.description":
    "Pour recevoir, tarifer, préparer, vérifier et remettre les prescriptions.",
  "helpSupport.categories.pharmacyPayments.title": "Paiements et revenus",
  "helpSupport.categories.pharmacyPayments.description":
    "Pour les lignes de paiement staging, la visibilité des revenus et les concepts de règlement.",
  "helpSupport.categories.pharmacyTeam.title": "Équipe et permissions",
  "helpSupport.categories.pharmacyTeam.description":
    "Pour inviter le personnel, attribuer les rôles et comprendre les permissions.",
  "helpSupport.categories.pharmacyMessages.title": "Messages et Fola",
  "helpSupport.categories.pharmacyMessages.description":
    "Pour les prompts Fola sûrs pour la pharmacie, la communication et l’appui opérationnel.",
  "helpSupport.categories.pharmacyNotifications.title": "Notifications et alertes",
  "helpSupport.categories.pharmacyNotifications.description":
    "Pour les alertes de prescription, les alertes de stock et les compteurs du tableau de bord.",
  "helpSupport.categories.pharmacyTechnical.title": "Assistance technique",
  "helpSupport.categories.pharmacyTechnical.description":
    "Pour l’accès, le routage, l’hydratation des données et les comportements inattendus.",
  "helpSupport.escalation.title": "Besoin d’aide supplémentaire ?",
  "helpSupport.escalation.body":
    "Si le guide ne résout pas le problème, envoyez une demande à l’assistance AfriMed™. Nous l’examinerons et l’orienterons vers la bonne équipe AfriMed™.",
  "helpSupport.requests.title": "Vos demandes d’assistance",
  "helpSupport.requests.description":
    "Les demandes apparaîtront ici après la création d’une demande d’assistance.",
  "helpSupport.quickLinks.title": "Liens du Centre d’aide",
  "helpSupport.quickLinks.helpCentre": "Centre d’aide",
  "helpSupport.quickLinks.gettingStarted": "Premiers pas",
  "helpSupport.quickLinks.technicalSupport": "Assistance technique",
  "helpSupport.quickLinks.appointments": "Rendez-vous",
  "helpSupport.quickLinks.prescriptionsMyehr": "Prescriptions et mon DME",
  "helpSupport.quickLinks.privacyConduct": "Confidentialité et conduite",
  "helpSupport.quickLinks.messagesFola": "Messages et Fola",
  "helpSupport.quickLinks.messagesEhrAccess": "Messages et accès EHR",
  "helpSupport.quickLinks.doctorPrescriptions": "Prescriptions, Fola et notes SOAP",
  "helpSupport.quickLinks.pharmacyDashboard": "Démarrage rapide pharmacie",
  "helpSupport.quickLinks.pharmacyStock": "Stock et inventaire",
  "helpSupport.quickLinks.pharmacyFulfilment": "Exécution des prescriptions",
  "helpSupport.quickLinks.pharmacyTeam": "Équipe et permissions",
  "helpSupport.contact.title": "Contacter l’assistance AfriMed™",
  "helpSupport.contact.address": "AfriMed™ Telehealth, Le Cap, Afrique du Sud",
  "helpSupport.safety.body":
    "Fola ne remplace pas les cliniciens. Les symptômes urgents ou relevant d’une urgence clinique doivent passer par les services d’urgence ou le contact clinicien, et non par les demandes d’assistance.",
  "helpSupport.roleToggle.aria": "Choisir le rôle d’assistance",
  "helpSupport.ticket.createTitle": "Nouvelle demande d’assistance",
  "helpSupport.ticket.createBody":
    "Expliquez ce qui vous bloque et nous l’orienterons vers la bonne équipe.",
  "helpSupport.ticket.creating": "Création...",
  "helpSupport.ticket.createAction": "Créer la demande d’assistance",
  "helpSupport.ticket.issueType": "Type de problème",
  "helpSupport.ticket.priority": "Priorité",
  "helpSupport.ticket.subject": "Objet",
  "helpSupport.ticket.subjectPlaceholder": "Décrivez brièvement le problème",
  "helpSupport.ticket.description": "Description",
  "helpSupport.ticket.descriptionPlaceholder":
    "Indiquez ce que vous avez essayé, ce que vous attendiez et ce que vous voyez maintenant.",
  "helpSupport.ticket.safetyNote":
    "Pour les symptômes cliniques urgents, contactez les services d’urgence ou votre clinicien directement. Les demandes d’assistance concernent l’accès, les dossiers, les rendez-vous, les paiements et les problèmes techniques.",
  "helpSupport.ticket.created": "Créée le {date}",
  "helpSupport.ticket.createSuccess": "Votre demande d’assistance a été créée.",
  "helpSupport.ticket.updated": "Mise à jour le {date}",
  "helpSupport.ticket.messages": "{count} messages",
  "helpSupport.ticket.priorityLabel": "Priorité {priority}",
  "helpSupport.ticket.status.open": "Ouverte",
  "helpSupport.ticket.status.in_progress": "En cours",
  "helpSupport.ticket.status.resolved": "Résolue",
  "helpSupport.ticket.status.closed": "Clôturée",
  "helpSupport.ticket.types.technical_issue.label": "Problème technique",
  "helpSupport.ticket.types.billing_inquiry.label": "Question de facturation",
  "helpSupport.ticket.types.account_access.label": "Accès au compte",
  "helpSupport.ticket.types.consultation_support.label": "Aide consultation",
  "helpSupport.ticket.types.general_inquiry.label": "Question générale",
  "helpSupport.ticket.types.feature_request.label": "Demande de fonctionnalité",
  "helpSupport.ticket.priorities.low.label": "Faible",
  "helpSupport.ticket.priorities.medium.label": "Moyenne",
  "helpSupport.ticket.priorities.high.label": "Élevée",
  "helpSupport.ticket.priorities.urgent.label": "Urgente",
  "common.close": "Fermer",
  "common.cancel": "Annuler",
};

function isFrenchLanguage(languageCode = "") {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function interpolateCopy(template, values) {
  if (typeof template !== "string" || !values || typeof values !== "object") {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : `{${key}}`
  );
}

function normalizeHelpRole(value) {
  const normalized = String(value || "").toLowerCase().trim();
  if (normalized === "doctor" || normalized === "pharmacy") return normalized;
  return "patient";
}

function getDefaultHelpRole(profile) {
  return normalizeHelpRole(profile?.role);
}

function getHelpRoleStorageKey(userId) {
  const owner = String(userId || "").trim();
  return owner ? `afrimed:help-support-role:${owner}` : "";
}

function normalizeSupportError(message, t) {
  const raw = String(message || "").trim();
  const lower = raw.toLowerCase();

  if (!raw) return "";

  if (lower.includes("permission denied") || lower.includes("authentication required")) {
    return t(
      "helpSupport.errors.permission",
      "Support requests could not be loaded right now. You can retry or create a new request if the issue continues."
    );
  }

  if (
    lower.includes("support_tickets_ticket_number_key") ||
    lower.includes("duplicate key value") ||
    lower.includes("violates unique constraint")
  ) {
    return t(
      "helpSupport.errors.ticketNumbering",
      "Support requests are temporarily unavailable while AfriMed™ support ticket numbering is being checked. Please use the contact details below if you need help now."
    );
  }

  if (
    lower.includes("row-level security") ||
    lower.includes("rls") ||
    lower.includes("postgrest") ||
    lower.includes("constraint")
  ) {
    return t(
      "helpSupport.errors.serviceUnavailable",
      "Support requests could not be completed right now. Please try again or use the contact details below."
    );
  }

  return raw;
}

function getStatusIcon(status) {
  return (
    {
      open: MessageCircle,
      in_progress: Clock,
      resolved: CheckCircle,
      closed: CheckCircle,
    }[status] || MessageCircle
  );
}

function SectionTitle({ title, description }) {
  return (
    <div>
      <h2 className="text-title-large text-on-surface">{title}</h2>
      {description ? (
        <p className="mt-2 max-w-[720px] text-body-large text-on-surface-var">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function HelpSupportBodyRail({ children }) {
  return (
    <div
      className="w-full min-w-0 overflow-x-hidden"
      data-help-support-canonical-body-rail
    >
      {children}
    </div>
  );
}

function SupportOptionRow({ option }) {
  const Icon = option.icon;

  return (
    <Link
      to={option.to}
      className="flex min-h-[76px] w-full items-center gap-3 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)] md:px-5"
    >
      <Icon className="h-5 w-5 shrink-0 text-on-surface-var" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-title-small text-on-surface">{option.title}</span>
        <span className="mt-1 block text-body-medium text-on-surface-var">
          {option.description}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-on-surface-var" aria-hidden />
    </Link>
  );
}

export default function HelpSupport() {
  const navigate = useNavigate();
  const handoff = useSupportLanguage();
  const { user, profile } = useAuth() || {};
  const { t: baseT, formatDate, languageCode } = useLanguage();
  const isFrench = isFrenchLanguage(languageCode);
  const t = React.useCallback(
    (key, fallback = "", values = undefined) => {
      const currentCopy = supportCopy[isFrench ? "fr" : "en"][key];
      if (currentCopy) return interpolateCopy(currentCopy, values);
      const localized =
        isFrench && Object.prototype.hasOwnProperty.call(HELP_SUPPORT_FR_COPY, key)
          ? HELP_SUPPORT_FR_COPY[key]
          : null;
      if (typeof localized === "string") {
        return interpolateCopy(localized, values);
      }
      return baseT(key, fallback, values);
    },
    [baseT, isFrench]
  );
  const defaultHelpRole = getDefaultHelpRole(profile);
  const helpRoleStorageKey = getHelpRoleStorageKey(user?.id);

  const [tickets, setTickets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [showNewTicket, setShowNewTicket] = React.useState(false);
  const [newTicket, setNewTicket] = React.useState(DEFAULT_NEW_TICKET);
  const [submitting, setSubmitting] = React.useState(false);
  const [loadFailure, setLoadFailure] = React.useState("");
  const [reload, setReload] = React.useState(0);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [helpRole, setHelpRoleState] = React.useState(defaultHelpRole);

  const supportOptions = supportService?.getSupportOptions?.() || {
    types: [],
    priorities: [],
  };
  const helpRoleOptions = isFrench ? HELP_ROLE_OPTIONS_FR : HELP_ROLE_OPTIONS;

  React.useEffect(() => {
    if (!helpRoleStorageKey) {
      setHelpRoleState(defaultHelpRole);
      return;
    }

    try {
      const saved = localStorage.getItem(helpRoleStorageKey);
      setHelpRoleState(saved ? normalizeHelpRole(saved) : defaultHelpRole);
    } catch {
      setHelpRoleState(defaultHelpRole);
    }
  }, [defaultHelpRole, helpRoleStorageKey]);

  const setHelpRole = React.useCallback(
    (next) => {
      const normalized = normalizeHelpRole(next);
      setHelpRoleState(normalized);
      if (!helpRoleStorageKey) return;
      try {
        localStorage.setItem(helpRoleStorageKey, normalized);
      } catch {
        // In-memory role selection is enough when storage is unavailable.
      }
    },
    [helpRoleStorageKey]
  );

  React.useEffect(() => {
    setTickets([]); setShowNewTicket(false); setNewTicket(DEFAULT_NEW_TICKET);
    setSuccess(""); setError("");
  }, [user?.id]);

  React.useEffect(() => {
    if (!user) { setLoading(false); return; }
    let active = true;
    async function loadSupportData() {
      setLoading(true); setError(""); setLoadFailure("");
      try {
        const result = await supportService.getUserTickets(filter === "all" ? null : filter);
        if (!active) return;
        if (result.error) {
          setLoadFailure(supportFailure(result.error));
          setError(isFrench ? "Impossible de charger l’assistance. Réessayez ou consultez l’aide à la connexion." : "Support could not be loaded. Retry or use the account-access guidance.");
        } else setTickets(result.data || []);
      } catch {
        if (active) { setLoadFailure("unavailable"); setError(isFrench ? "L’assistance est temporairement indisponible. Réessayez." : "Support is temporarily unavailable. Please retry."); }
      } finally { if (active) setLoading(false); }
    }
    void loadSupportData();
    return () => { active = false; };
  }, [filter, isFrench, user?.id, reload]);

  const typeOptions = React.useMemo(
    () =>
      (supportOptions?.types || []).map((option) => ({
        ...option,
        label: t(
          `helpSupport.ticket.types.${option?.value}.label`,
          option?.label || option?.value || ""
        ),
      })),
    [supportOptions?.types, t]
  );

  const priorityOptions = React.useMemo(
    () =>
      (supportOptions?.priorities || []).map((option) => ({
        ...option,
        label: t(
          `helpSupport.ticket.priorities.${option?.value}.label`,
          option?.label || option?.value || ""
        ),
      })),
    [supportOptions?.priorities, t]
  );

  const filterOptions = React.useMemo(
    () => [
      { value: "all", label: t("helpSupport.filters.all", "All requests") },
      { value: "open", label: t("helpSupport.filters.open", "Open") },
      {
        value: "in_progress",
        label: t("helpSupport.filters.inProgress", "In progress"),
      },
      {
        value: "resolved",
        label: t("helpSupport.filters.resolved", "Resolved"),
      },
      { value: "closed", label: t("helpSupport.filters.closed", "Closed") },
    ],
    [t]
  );

  const supportCategoryOptions = React.useMemo(() => {
    if (helpRole === "doctor") {
      return [
            {
              key: "doctor-profile",
              icon: Stethoscope,
              title: t("helpSupport.categories.doctorProfile.title", "Doctor profile setup"),
              description: t(
                "helpSupport.categories.doctorProfile.description",
                "For clinician profile, specialty, languages, practice, and Settings setup."
              ),
              type: "general_inquiry",
              to: "/help/doctor-profile-setup",
            },
            {
              key: "doctor-verification",
              icon: ShieldCheck,
              title: t("helpSupport.categories.doctorVerification.title", "Verification and credentials"),
              description: t(
                "helpSupport.categories.doctorVerification.description",
                "For ID/passport, registration, credential upload, and pending verification questions."
              ),
              type: "account_access",
              to: "/help/doctor-verification-credentials",
            },
            {
              key: "doctor-availability",
              icon: Calendar,
              title: t("helpSupport.categories.doctorAvailability.title", "Availability and consultancies"),
              description: t(
                "helpSupport.categories.doctorAvailability.description",
                "For practice/facility setup, services, consultancies, and availability."
              ),
              type: "consultation_support",
              to: "/help/doctor-availability-consultancies",
            },
            {
              key: "doctor-appointments",
              icon: Calendar,
              title: t("helpSupport.categories.doctorAppointments.title", "Appointments"),
              description: t(
                "helpSupport.categories.doctorAppointments.description",
                "For reviewing appointment requests, visit details, and doctor dashboard scheduling."
              ),
              type: "consultation_support",
              to: "/help/doctor-appointments",
            },
            {
              key: "doctor-messages",
              icon: MessageCircle,
              title: t("helpSupport.categories.doctorMessages.title", "Messages, referrals, and EHR access"),
              description: t(
                "helpSupport.categories.doctorMessages.description",
                "For patient messages, referral workflows, and requesting EHR access."
              ),
              type: "general_inquiry",
              to: "/help/doctor-messaging-ehr",
            },
            {
              key: "doctor-prescriptions",
              icon: Pill,
              title: t("helpSupport.categories.doctorPrescriptions.title", "Prescriptions, Fola, and SOAP notes"),
              description: t(
                "helpSupport.categories.doctorPrescriptions.description",
                "For prescriptions, clinician Fola support, VitalRep, and note workflows."
              ),
              type: "general_inquiry",
              to: "/help/doctor-prescriptions-notes",
            },
          ];
    }

    if (helpRole === "pharmacy") {
      return [
        {
          key: "pharmacy-profile",
          icon: ShieldCheck,
          title: t("helpSupport.categories.pharmacyProfile.title", "Pharmacy profile setup"),
          description: t(
            "helpSupport.categories.pharmacyProfile.description",
            "For branch profile, verification, payout readiness, launch readiness, and owner access."
          ),
          type: "account_access",
          to: "/pharmacy/help/pharmacy-profile-verification",
        },
        {
          key: "pharmacy-stock",
          icon: Pill,
          title: t("helpSupport.categories.pharmacyStock.title", "Stock and inventory"),
          description: t(
            "helpSupport.categories.pharmacyStock.description",
            "For seeded stock, adding or editing inventory, and stock visibility."
          ),
          type: "general_inquiry",
          to: "/pharmacy/help/inventory-management",
        },
        {
          key: "pharmacy-fulfilment",
          icon: FileHeart,
          title: t("helpSupport.categories.pharmacyFulfilment.title", "Prescription fulfilment"),
          description: t(
            "helpSupport.categories.pharmacyFulfilment.description",
            "For receiving, pricing, preparing, verifying, and handing over prescriptions."
          ),
          type: "general_inquiry",
          to: "/pharmacy/help/prescription-fulfilment",
        },
        {
          key: "pharmacy-payments",
          icon: CreditCard,
          title: t("helpSupport.categories.pharmacyPayments.title", "Payments and revenue"),
          description: t(
            "helpSupport.categories.pharmacyPayments.description",
            "For staging payment rows, revenue visibility, and settlement concepts."
          ),
          type: "billing_inquiry",
          to: "/pharmacy/help/payments-settlement",
        },
        {
          key: "pharmacy-team",
          icon: UserRound,
          title: t("helpSupport.categories.pharmacyTeam.title", "Team members and permissions"),
          description: t(
            "helpSupport.categories.pharmacyTeam.description",
            "For inviting staff, assigning roles, and understanding permissions."
          ),
          type: "account_access",
          to: "/pharmacy/help/team-members-permissions",
        },
        {
          key: "pharmacy-messages",
          icon: MessageCircle,
          title: t("helpSupport.categories.pharmacyMessages.title", "Messages and Fola"),
          description: t(
            "helpSupport.categories.pharmacyMessages.description",
            "For pharmacy-safe Fola prompts, communication, and operational support."
          ),
          type: "general_inquiry",
          to: "/pharmacy/help/messages-fola",
        },
        {
          key: "pharmacy-notifications",
          icon: Clock,
          title: t("helpSupport.categories.pharmacyNotifications.title", "Notifications and alerts"),
          description: t(
            "helpSupport.categories.pharmacyNotifications.description",
            "For prescription alerts, stock alerts, and dashboard counters."
          ),
          type: "technical_issue",
          to: "/pharmacy/help/notifications-alerts",
        },
        {
          key: "pharmacy-technical",
          icon: Wrench,
          title: t("helpSupport.categories.pharmacyTechnical.title", "Technical support"),
          description: t(
            "helpSupport.categories.pharmacyTechnical.description",
            "For access, routing, hydration, and unexpected behaviour."
          ),
          type: "technical_issue",
          to: "/pharmacy/help/technical-support",
        },
      ];
    }

    return [
      {
        key: "account",
        icon: UserRound,
        title: t("helpSupport.categories.account.title", "Account access"),
        description: t(
          "helpSupport.categories.account.description",
          "For login, OTP, password, email, or phone verification issues."
        ),
        type: "account_access",
        to: "/help/getting-started",
      },
      {
        key: "appointments",
        icon: Calendar,
        title: t("helpSupport.categories.appointments.title", "Appointments"),
        description: t(
          "helpSupport.categories.appointments.description",
          "For booking, changing, video visit, clinician, or dependant appointment support."
        ),
        type: "consultation_support",
        to: "/help/appointments",
      },
      {
        key: "ehr",
        icon: FileHeart,
        title: t("helpSupport.categories.ehr.title", "Prescriptions & myEHR"),
        description: t(
          "helpSupport.categories.ehr.description",
          "For prescription access, health record visibility, verification, or download questions."
        ),
        type: "general_inquiry",
        to: "/help/prescriptions-myehr",
      },
      {
        key: "payments",
        icon: CreditCard,
        title: t("helpSupport.categories.payments.title", "Payments & insurance"),
        description: t(
          "helpSupport.categories.payments.description",
          "For payment method, sponsor link, receipt, invoice, or insurance-selection questions."
        ),
        type: "billing_inquiry",
        to: "/help/payments-insurance",
      },
      {
        key: "messages",
        icon: MessageCircle,
        title: t("helpSupport.categories.messages.title", "Messages & Fola"),
        description: t(
          "helpSupport.categories.messages.description",
          "For message threads, Fola support, language support, and what Fola can or cannot do."
        ),
        type: "general_inquiry",
        to: "/help/messages-fola",
      },
      {
        key: "technical",
        icon: Wrench,
        title: t("helpSupport.categories.technical.title", "Technical support"),
        description: t(
          "helpSupport.categories.technical.description",
          "For browser, device, notification, upload, or display issues."
        ),
        type: "technical_issue",
        to: "/help/technical-support",
      },
    ];
  }, [helpRole, t]);

  const filteredTickets = React.useMemo(
    () =>
      (tickets || []).filter((ticket) => {
        if (!searchQuery) return true;
        const needle = searchQuery.toLowerCase();
        return (
          String(ticket?.subject || "")
            .toLowerCase()
            .includes(needle) ||
          String(ticket?.ticket_number || "")
            .toLowerCase()
            .includes(needle)
        );
      }),
    [searchQuery, tickets]
  );

  const displayError = normalizeSupportError(error, t);
  const supportAccessBlocked = !supportAvailability({ signedIn: Boolean(user?.id), loading, failure: loadFailure });

  const formatSupportDate = React.useCallback(
    (value) => {
      const next = value ? new Date(value) : null;
      if (!next || Number.isNaN(next.getTime())) {
        return t("helpSupport.common.recently", "Recently");
      }
      return formatDate(next, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    [formatDate, t]
  );

  function openRequestForm(type = handoff.topic || "general_inquiry") {
    if (supportAccessBlocked) return;
    setNewTicket((current) => ({ ...current, type }));
    setError("");
    setSuccess("");
    setShowNewTicket(true);
  }

  function cancelRequestForm() {
    setNewTicket(DEFAULT_NEW_TICKET);
    setError("");
    setSuccess("");
    setShowNewTicket(false);
  }

  async function handleSubmitTicket(event) {
    event?.preventDefault();

    if (supportAccessBlocked || submitting) return;
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      setError(
        t(
          "helpSupport.errors.requiredFields",
          "Please fill in all required fields."
        )
      );
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const { data, error: createError } =
        await supportService?.createTicket({
          ...newTicket,
          requesterRole: helpRole,
          category: newTicket.type,
          source: "help-support",
          sourceRoute:
            typeof window !== "undefined"
              ? `${window.location.pathname || "/help-support"}${window.location.search || ""}`
              : "/help-support",
          metadata: {
            support_role: helpRole,
            language: languageCode,
            entry_source: handoff.source || "application",
            route: "/help-support",
          },
        });
      if (createError) {
        if (["auth", "denied"].includes(supportFailure(createError))) setLoadFailure(supportFailure(createError));
        setError(
          normalizeSupportError(
            createError?.message ||
              t(
                "helpSupport.errors.createTicket",
                "Failed to create support request."
              ),
            t
          )
        );
        return;
      }

      setTickets((current) => [data, ...current]);
      setSuccess(
        t("helpSupport.ticket.createSuccess", "Your support request has been created.")
      );
      setNewTicket(DEFAULT_NEW_TICKET);
      setShowNewTicket(false);
    } catch {
      setError(isFrench ? "Impossible de créer la demande. Réessayez." : "The request could not be created. Please retry.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!user) {
    return (
      <HelpSupportBodyRail>
        <div className="mx-auto max-w-[560px]">
          <SettingsCard className="px-6 py-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[18px] bg-[rgba(29,78,216,0.10)] text-[rgb(var(--afrimed-primary))]">
              <Info className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-title-large text-on-surface">
              {t("helpSupport.auth.title", "Authentication required")}
            </h1>
            <p className="mt-2 text-body-large text-on-surface-var">
              {t(
                "helpSupport.auth.body",
                "Please sign in to access help and support."
              )}
            </p>
            <div className="mt-6 flex justify-center">
              <Link to={`/login?next=${encodeURIComponent(`/help-support${typeof window !== "undefined" ? window.location.search : ""}`)}`}>
                <Button>{t("helpSupport.auth.signIn", "Sign in")}</Button>
              </Link>
            </div>
          </SettingsCard>
        </div>
      </HelpSupportBodyRail>
    );
  }

  return (
    <HelpSupportBodyRail>
      <TopBarCenter
        title={t("helpSupport.title", "Help & support")}
        onBack={() => navigate(-1)}
        titleClassName="text-title-large text-on-surface"
      />

      <div className="space-y-6">
        <section>
          <div className="text-label-large text-[rgb(var(--afrimed-primary))]">
            {t("helpSupport.eyebrow", "Help Centre support")}
          </div>
          <p className="mt-2 max-w-[760px] text-body-large text-on-surface-var">
            {t(
              "helpSupport.subtitle",
              "Get help with your AfriMed™ account, appointments, messages, prescriptions, payments, and access."
            )}
          </p>
          <div className="mt-5 flex w-full min-w-0 justify-center">
            <AfriSwitch
              value={helpRole}
              onChange={setHelpRole}
              options={helpRoleOptions}
              ariaLabel={t("helpSupport.roleToggle.aria", "Choose support role")}
              className="w-full max-w-[360px] justify-between bg-[rgb(var(--clr-surface-cont-highest))] [&_button]:min-w-0 [&_button]:flex-1"
            />
          </div>
        </section>

        {displayError ? (
          <div className="rounded-[12px] border border-[rgba(var(--schemes-critical),0.24)] bg-[var(--state-critical-08)] px-4 py-4 text-body-medium text-on-surface">
            <div className="flex gap-3">
              <ShieldAlert
                className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--schemes-critical))]"
                aria-hidden
              />
              <div>
                <div className="text-title-small text-[rgb(var(--schemes-critical))]">
                  {supportAccessBlocked
                    ? t("helpSupport.accessPending.title", "Support unavailable")
                    : t("helpSupport.notice.title", "Support notice")}
                </div>
                <p className="mt-1 text-body-medium text-on-surface-var">
                  {displayError}
                </p>
                {loadFailure && <Button variant="tonal" className="mt-3" onClick={() => setReload(n => n + 1)}>{isFrench ? "Réessayer" : "Retry"}</Button>}
              </div>
            </div>
          </div>
        ) : null}

        {success ? (
          <div className="rounded-[12px] border border-[rgba(var(--schemes-success),0.28)] bg-[var(--state-success-08)] px-4 py-3 text-body-medium text-on-surface">
            {success}
          </div>
        ) : null}

        <section className="space-y-3">
          <SectionTitle
            title={t("helpSupport.supportOptions.title", "What do you need help with?")}
            description={
              helpRole === "doctor"
                ? t(
                    "helpSupport.supportOptions.doctorDescription",
                    "Choose the clinician area closest to your issue. AfriMed™ support helps with doctor profiles, verification, appointments, messages, records, and technical questions."
                  )
                : helpRole === "pharmacy"
                ? t(
                    "helpSupport.supportOptions.pharmacyDescription",
                    "Choose the pharmacy area closest to your issue. AfriMed™ support helps with onboarding, branches, stock, prescription fulfilment, payments, team access, and technical questions."
                  )
                : t(
                    "helpSupport.supportOptions.description",
                    "Choose the area closest to your issue. AfriMed™ support helps with access, records, appointments, payments, and technical questions."
                  )
            }
          />

          <SettingsCard className="overflow-hidden px-0 py-0 shadow-[var(--elev-1)]">
            {supportCategoryOptions.map((option, index) => (
              <React.Fragment key={option.key}>
                <SupportOptionRow option={option} />
                {index !== supportCategoryOptions.length - 1 ? (
                  <div
                    className="h-px"
                    style={{
                      marginLeft: 56,
                      background: "var(--Schemes-Outline-Variant,#E6E7EB)",
                    }}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </SettingsCard>
        </section>

        <section className="w-full min-w-0 px-1 py-2 md:px-0">
          <div className="min-w-0">
            <h2 className="break-words text-title-large text-on-surface">
              {t("helpSupport.escalation.title", "Still need help?")}
            </h2>
            <p className="mt-2 max-w-[920px] break-words text-body-medium text-on-surface-var">
              {loading ? (isFrench ? "Vérification de l’accès à l’assistance…" : "Checking support access…") : loadFailure ? (isFrench ? "L’assistance n’a pas pu être chargée. Réessayez ou consultez les guides." : "Support could not be loaded. Retry or browse the guides.") : t(
                "helpSupport.escalation.body",
                "If the guidance does not solve the issue, send AfriMed™ support a request. We’ll review it and route it to the right AfriMed™ support team."
              )}
            </p>
          </div>
          <div className="mt-4 grid w-full min-w-0 gap-3 sm:max-w-[680px] sm:grid-cols-2">
            <Button
              className="h-12 w-full whitespace-nowrap px-5"
              onClick={() => openRequestForm()}
              disabled={supportAccessBlocked}
              iconName="MessageSquarePlus"
              iconPosition="left"
              title={
                supportAccessBlocked
                  ? t(
                      "helpSupport.actions.supportPendingTitle",
                      "Support requests are not enabled for this account yet."
                    )
                  : undefined
              }
            >
              {loading ? (isFrench ? "Chargement…" : "Loading…") : supportAccessBlocked
                ? t("helpSupport.actions.supportPending", "Support access pending")
                    : t("helpSupport.actions.newRequest", "New support request")}
            </Button>
            <Link to={helpRole === "pharmacy" ? "/pharmacy/help" : "/help"} className="block w-full min-w-0">
              <Button
                variant="tonal"
                className="h-12 w-full whitespace-nowrap px-5"
                iconName="BookOpen"
                iconPosition="left"
              >
                {t("helpSupport.actions.browseHelpCentre", "Browse Help Centre")}
              </Button>
            </Link>
          </div>
        </section>

        {showNewTicket && !supportAccessBlocked ? (
          <section className="space-y-5 px-1 py-2 md:px-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-title-large text-on-surface">
                  {t("helpSupport.ticket.createTitle", "New support request")}
                </h2>
                <p className="mt-1 text-body-medium text-on-surface-var">
                  {t(
                    "helpSupport.ticket.createBody",
                    "Tell us what is blocking you and we’ll route it to the right team."
                  )}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelRequestForm}
                disabled={submitting}
                iconName="X"
                aria-label={t("common.close", "Close")}
              />
            </div>

            <form onSubmit={handleSubmitTicket} className="mt-5 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-label-large text-on-surface">
                    {t("helpSupport.ticket.issueType", "Type of issue")}
                  </span>
                  <Select
                    value={newTicket.type}
                    onValueChange={(value) =>
                      setNewTicket((current) => ({ ...current, type: value }))
                    }
                    options={typeOptions}
                    className="w-full"
                  />
                </label>

                <label className="block">
                  <span className="mb-2 block text-label-large text-on-surface">
                    {t("helpSupport.ticket.priority", "Priority")}
                  </span>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(value) =>
                      setNewTicket((current) => ({ ...current, priority: value }))
                    }
                    options={priorityOptions}
                    className="w-full"
                  />
                </label>
              </div>

              <TextField56
                tone="surface"
                label={t("helpSupport.ticket.subject", "Subject")}
                value={newTicket.subject}
                onChange={(value) =>
                  setNewTicket((current) => ({
                    ...current,
                    subject: value,
                  }))
                }
                placeholder={t(
                  "helpSupport.ticket.subjectPlaceholder",
                  "Briefly describe the issue"
                )}
                inputProps={{ required: true }}
              />

              <label className="block">
                <span className="mb-2 block text-label-large text-on-surface">
                  {t("helpSupport.ticket.description", "Description")}
                </span>
                <textarea
                  value={newTicket.description}
                  onChange={(event) =>
                    setNewTicket((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  rows={5}
                  className="w-full rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-large text-on-surface outline-none focus:border-[rgba(29,78,216,0.35)] focus:ring-2 focus:ring-[rgba(29,78,216,0.14)]"
                  placeholder={t(
                    "helpSupport.ticket.descriptionPlaceholder",
                    "Share what you tried, what you expected, and what you are seeing now."
                  )}
                  required
                />
              </label>

              <div className="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-medium text-on-surface-var">
                {t(
                  "helpSupport.ticket.safetyNote",
                  "For urgent clinical symptoms, contact emergency services or your clinician directly. Support requests are for access, records, appointments, payments, and technical issues."
                )}
              </div>

              <div className="flex flex-col gap-3 md:flex-row">
                <Button
                  type="submit"
                  size="cta"
                  className="w-full whitespace-nowrap px-5 md:w-auto"
                  disabled={submitting}
                >
                  {submitting
                    ? t("helpSupport.ticket.creating", "Creating...")
                    : t("helpSupport.ticket.createAction", "Create support request")}
                </Button>
                <Button
                  type="button"
                  variant="tonal"
                  size="cta"
                  className="w-full whitespace-nowrap px-5 md:w-auto"
                  disabled={submitting}
                  onClick={cancelRequestForm}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
              </div>
            </form>
          </section>
        ) : null}

        <section>
          <h2 className="text-title-large text-on-surface">
            {t("helpSupport.requests.title", "Your support requests")}
          </h2>
          <p className="mt-2 max-w-[760px] text-body-medium text-on-surface-var">
            {loadFailure ? (isFrench ? "L’historique n’a pas pu être actualisé. Les demandes déjà chargées restent affichées." : "History could not be refreshed. Previously loaded requests remain visible.") : t(
              "helpSupport.requests.description",
              "Requests appear here after you create a support request."
            )}
          </p>
        </section>

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="space-y-3">

            {tickets.length ? (
              <div className="relative z-20 flex flex-col gap-3 py-1 md:flex-row md:items-center">
                <label className="relative flex-1">
                  <span className="sr-only">
                    {t("helpSupport.filters.searchPlaceholder", "Search requests")}
                  </span>
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-var" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t(
                      "helpSupport.filters.searchPlaceholder",
                      "Search requests"
                    )}
                    className="h-12 w-full rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] pl-11 pr-4 text-body-large text-on-surface outline-none focus:border-[rgba(29,78,216,0.35)] focus:ring-2 focus:ring-[rgba(29,78,216,0.14)]"
                  />
                </label>
                <Select
                  value={filter}
                  onValueChange={setFilter}
                  options={filterOptions}
                  className="w-full md:w-[220px]"
                  triggerClassName="bg-[rgb(var(--clr-surface-cont-highest))] shadow-none"
                />
              </div>
            ) : null}

            {loading ? (
              <SettingsCard className="px-6 py-8 text-center">
                <div className="mx-auto h-7 w-7 animate-spin rounded-full border-b-2 border-[rgb(var(--afrimed-primary))]" />
                <div className="mt-4 text-body-large text-on-surface-var">
                  {t("helpSupport.loading", "Loading support requests...")}
                </div>
              </SettingsCard>
            ) : filteredTickets.length ? (
              <div className="space-y-3">
                {filteredTickets.map((ticket) => {
                  const StatusIcon = getStatusIcon(ticket?.status);
                  const statusLabel = t(
                    `helpSupport.ticket.status.${ticket?.status}`,
                    String(ticket?.status || "")
                      .replaceAll("_", " ")
                      .trim()
                  );
                  const priorityLabel = t(
                    `helpSupport.ticket.priorities.${ticket?.priority}.label`,
                    String(ticket?.priority || "")
                  );

                  return (
                    <SettingsCard key={ticket?.id} className="px-5 py-5 md:px-6">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-label-medium text-on-surface-var">
                            #{ticket?.ticket_number}
                          </span>
                          <span
                            className={[
                              "inline-flex items-center gap-1 rounded-[8px] border px-2.5 py-1 text-label-medium",
                              supportStatusBadgeClass(ticket?.status),
                            ].join(" ")}
                          >
                            <StatusIcon className="h-3 w-3" aria-hidden />
                            <span>{statusLabel}</span>
                          </span>
                          <span
                            className={[
                              "text-label-medium",
                              supportPriorityBadgeClass(ticket?.priority),
                            ].join(" ")}
                          >
                            {t("helpSupport.ticket.priorityLabel", "{priority} priority", {
                              priority: priorityLabel,
                            })}
                          </span>
                        </div>

                        <h3 className="mt-3 text-title-large text-on-surface">
                          {ticket?.subject}
                        </h3>
                        <p className="mt-2 text-body-large text-on-surface-var">
                          {ticket?.description}
                        </p>

                        <div className="mt-4 flex flex-wrap items-center gap-4 text-body-small text-on-surface-var">
                          <span>
                            {t("helpSupport.ticket.created", "Created {date}", {
                              date: formatSupportDate(ticket?.created_at),
                            })}
                          </span>
                          {Number(ticket?.message_count || 0) > 0 ? (
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" aria-hidden />
                              <span>
                                {t("helpSupport.ticket.messages", "{count} messages", {
                                  count: Number(ticket?.message_count || 0),
                                })}
                              </span>
                            </span>
                          ) : null}
                          {ticket?.updated_at &&
                          ticket?.updated_at !== ticket?.created_at ? (
                            <span>
                              {t("helpSupport.ticket.updated", "Updated {date}", {
                                date: formatSupportDate(ticket?.updated_at),
                              })}
                            </span>
                          ) : null}
                        </div>
                        <SupportRequestThread ticketId={ticket.id} isFrench={isFrench} />
                      </div>
                    </SettingsCard>
                  );
                })}
              </div>
            ) : (
              <SettingsCard className="px-5 py-7 text-center md:px-6">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[16px] bg-[rgba(29,78,216,0.10)] text-[rgb(var(--afrimed-primary))]">
                  <LifeBuoy className="h-6 w-6" aria-hidden />
                </div>
                <h3 className="mt-4 text-title-large text-on-surface">
                  {searchQuery || filter !== "all"
                    ? t("helpSupport.empty.filteredTitle", "No matching requests")
                    : t("helpSupport.empty.title", "No support requests yet.")}
                </h3>
                <p className="mx-auto mt-2 max-w-[520px] text-body-large text-on-surface-var">
                  {searchQuery || filter !== "all"
                    ? t(
                        "helpSupport.empty.filtered",
                        "No requests match your current search or filter."
                      )
                    : t(
                        "helpSupport.empty.default",
                        "Create a support request if you need AfriMed™ Support to review something."
                      )}
                </p>
              </SettingsCard>
            )}
          </section>

          <aside className="space-y-4">
            <SettingsCard className="px-5 py-5">
              <h2 className="text-title-medium font-normal text-on-surface">
                {t("helpSupport.contact.title", "Contact AfriMed™ support")}
              </h2>
              <div className="mt-4 space-y-4 text-body-medium text-on-surface-var">
                <a
                  href={supportContact.contactUrls[isFrench ? "fr" : "en"]}
                  className="flex items-start gap-3 break-all text-on-surface-var focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]"
                >
                  <LifeBuoy className="mt-0.5 h-5 w-5 shrink-0 text-on-surface-var" />
                  <span>{isFrench ? "Assistance" : "Support"}</span>
                </a>
                <a href={`https://afrimed.tech/${isFrench ? "fr/" : ""}help/#account-access`}>
                  {isFrench ? "Aide à la connexion" : "Account-access help"}
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-on-surface-var" />
                  <span>
                    {t(
                      "helpSupport.contact.address",
                      "AfriMed™ Telehealth, Cape Town, South Africa"
                    )}
                  </span>
                </div>
              </div>
            </SettingsCard>

            <div className="rounded-[14px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4">
              <div className="flex gap-3">
                <Info
                  className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--afrimed-primary))]"
                  aria-hidden
                />
                <p className="text-body-medium text-on-surface-var">
                  {t(
                    "helpSupport.safety.body",
                    "Fola does not replace clinicians. Emergency or urgent clinical symptoms should use emergency services or clinician contact, not support requests."
                  )}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </HelpSupportBodyRail>
  );
}
